import { forwardRef, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { avatarVariants, type AvatarProps } from './variants';

/**
 * Default user silhouette shown when no `src` is provided.
 */
const AvatarFallbackIcon = () => (
  <svg viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg" className="size-full" aria-hidden="true">
    <rect x="1" y="1" width="44" height="44" rx="22" className="fill-input-soft stroke-input-soft" strokeWidth="2" />
    <path
      d="M22.7459 14.0157C18.2982 14.0157 14.6816 17.7086 14.6816 22.25C14.6816 25.5971 16.6361 28.6143 19.6679 29.8871C22.6843 31.1757 26.1778 30.4686 28.4862 28.0957C30.7947 25.7386 31.4872 22.1714 30.2406 19.0914C28.9941 15.9957 26.0392 14 22.7612 14L22.7459 14.0157Z"
      className="fill-purple"
    />
    <path
      d="M22.281 32.2656C28.9385 32.2656 32.6621 35.2299 35.463 38.7656C31.5064 42.796 19.7935 46.2656 10 38.2656C12.8163 34.7456 17.818 32.2656 22.281 32.2656Z"
      className="fill-purple"
    />
  </svg>
);

/**
 * Circular user avatar.
 *
 * Renders an `<img>` when `src` is provided; otherwise — or when the image
 * fails to load (stale/blocked profile-photo URL) — falls back to a user
 * silhouette icon. Use this for the current user (e.g. top bar); for
 * thumbnails of non-user entities use `EmptyCircle`.
 *
 * @example
 * ```tsx
 * <Avatar src="/user.jpg" size="lg" alt="User" />
 * <Avatar size="lg" />
 * ```
 */
const Avatar = forwardRef<HTMLDivElement, AvatarProps>(({ className, size, src, alt, ...props }, ref) => {
  const [failed, setFailed] = useState(false);

  // A new src deserves a fresh attempt. Intentional state sync on prop change.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFailed(false);
  }, [src]);

  return (
    <div ref={ref} className={cn(avatarVariants({ size }), className)}>
      {src && !failed ? (
        <img src={src} alt={alt ?? ''} className="size-full object-cover" onError={() => setFailed(true)} {...props} />
      ) : (
        <AvatarFallbackIcon />
      )}
    </div>
  );
});
Avatar.displayName = 'Avatar';

export { Avatar };
